import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { teamsApi, type TeamTaskRow } from './api'
import { handleApiError } from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'
import type { Team } from '@/types/entities'
import type { ListParams } from '@/lib/api/types'

// Query keys
export const teamKeys = {
    all: ['teams'] as const,
    lists: () => [...teamKeys.all, 'list'] as const,
    list: (params: ListParams) => [...teamKeys.lists(), params] as const,
    details: () => [...teamKeys.all, 'detail'] as const,
    detail: (id: number) => [...teamKeys.details(), id] as const,
    mobilisationTasks: (teamId: number) => [...teamKeys.detail(teamId), 'mobilisation-tasks'] as const,
    technicalChecklist: (teamId: number) => [...teamKeys.detail(teamId), 'technical-checklist'] as const,
}

// Teams hooks
export function useTeams(params?: ListParams) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: teamKeys.list(params || {}),
        queryFn: () => teamsApi.list(params),
        select: (response) => response.data,
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useTeam(id: number) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: teamKeys.detail(id),
        queryFn: () => teamsApi.get(id),
        select: (response) => response.data,
        enabled: !!id && isAuthenticated && !mfaRequired,
    })
}

export function useCreateTeam() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: Omit<Team, 'id' | 'createdAt' | 'updatedAt'>) =>
            teamsApi.create(data),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: teamKeys.lists() })
            toast({
                title: 'Team Created',
                description: `${response.data.teamName} has been created successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useUpdateTeam() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<Omit<Team, 'id' | 'createdAt' | 'updatedAt'>> }) =>
            teamsApi.update(id, data),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: teamKeys.lists() })
            queryClient.invalidateQueries({ queryKey: teamKeys.detail(variables.id) })
            toast({
                title: 'Team Updated',
                description: `${response.data.teamName} has been updated successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useDeleteTeam() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => teamsApi.delete(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: teamKeys.lists() })
            queryClient.removeQueries({ queryKey: teamKeys.detail(id) })
            toast({
                title: 'Team Deleted',
                description: 'Team has been deleted successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useTeamMobilisationTasks(teamId: number | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: teamKeys.mobilisationTasks(teamId ?? 0),
        queryFn: () => teamsApi.getMobilisationTasks(teamId!),
        select: (response) => (response?.data?.data ?? []) as TeamTaskRow[],
        enabled: !!teamId && isAuthenticated && !mfaRequired,
    })
}

export function useSaveTeamMobilisationTasks() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ teamId, tasks }: { teamId: number; tasks: TeamTaskRow[] }) =>
            teamsApi.saveMobilisationTasks(teamId, tasks),
        onSuccess: (response, { teamId }) => {
            const data = response?.data?.data ?? []
            queryClient.setQueryData(teamKeys.mobilisationTasks(teamId), { data: { data } })
            queryClient.invalidateQueries({ queryKey: teamKeys.mobilisationTasks(teamId) })
        },
    })
}

export function useTeamTechnicalChecklist(teamId: number | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: teamKeys.technicalChecklist(teamId ?? 0),
        queryFn: () => teamsApi.getTechnicalChecklist(teamId!),
        select: (response) => (response?.data?.data ?? []) as TeamTaskRow[],
        enabled: !!teamId && isAuthenticated && !mfaRequired,
    })
}

export function useSaveTeamTechnicalChecklist() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ teamId, tasks }: { teamId: number; tasks: TeamTaskRow[] }) =>
            teamsApi.saveTechnicalChecklist(teamId, tasks),
        onSuccess: (response, { teamId }) => {
            const data = response?.data?.data ?? []
            queryClient.setQueryData(teamKeys.technicalChecklist(teamId), { data: { data } })
            queryClient.invalidateQueries({ queryKey: teamKeys.technicalChecklist(teamId) })
        },
    })
}
